import { useQuery } from "@tanstack/react-query";
import { DetailRow } from "@/components/shared/finance/DetailRow";
import { createPeekContent, PeekHeader } from "@/components/shared/relationships/peek-content";
import { registerPeekType } from "@/components/shared/relationships/peek-registry";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { shippingKeys } from "@/features/operations/hooks/query-keys";
import { getShippingRecord } from "@/features/operations/hooks/operations-client";
import { ABSENT_VALUE, formatDate, formatDocumentUid } from "@/lib/format";

interface ShipmentSummary {
  date: string | null;
  id: string;
  recipient: string | null;
  status: string | null;
  tracking_number: string | null;
  uid: string | null;
}

function useShipmentDetail(id: string | undefined) {
  return useQuery<ShipmentSummary>({
    enabled: Boolean(id),
    queryFn: () => getShippingRecord(id as string),
    queryKey: shippingKeys.detail(id || "missing"),
  });
}

function ShipmentContent({ shipment }: { shipment: ShipmentSummary }) {
  return (
    <>
      <PeekHeader
        badge={
          shipment.status ? (
            <Badge variant="outline">{shipment.status.replaceAll("_", " ")}</Badge>
          ) : null
        }
        title={formatDocumentUid(shipment.uid, shipment.id)}
      />
      <Separator />
      <div className="space-y-2">
        <DetailRow label="Date" value={formatDate(shipment.date)} />
        <DetailRow label="Recipient" value={shipment.recipient || ABSENT_VALUE} />
        <DetailRow label="Tracking Number" value={shipment.tracking_number || ABSENT_VALUE} />
      </div>
    </>
  );
}

/** Shipment quick-view content, registered on import via `src/app/peek-registrations.ts`. */
export const ShipmentPeekContent = createPeekContent({
  emptyMessage: "No shipment found",
  render: (shipment) => <ShipmentContent shipment={shipment} />,
  useDetail: useShipmentDetail,
});

registerPeekType("shipment", ShipmentPeekContent);
